import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { type BillingSession, billingSessionSchema } from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient);
type SessionState =
  | { scope: VisitScope; kind: "pending" | "error" }
  | { scope: VisitScope; kind: "success"; session: BillingSession };
export const useStripeGetSession = () => {
  const scope = useVisitScope();
  const { mutateAsync, reset } = useMutation({
    ...orpc.createBillingSession.mutationOptions({
      context: { expectedUserUid: scope?.uid },
    }),
    retry: false,
    gcTime: 0,
  });
  const [state, setState] = useState<SessionState | null>(null);
  const active = useRef({ scope });
  if (active.current.scope !== scope) active.current = { scope };
  const lifetime = active.current;
  const mounted = useRef(true);
  const pending = useRef<{
    lifetime: typeof lifetime;
    promise: Promise<void>;
  } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const fetch = useCallback((): Promise<void> => {
    const current = () =>
      mounted.current &&
      active.current === lifetime &&
      !!scope &&
      getVisitScope() === scope;
    if (!scope || !current()) return Promise.resolve();
    if (pending.current?.lifetime === lifetime) return pending.current.promise;
    setState({ scope, kind: "pending" });
    const promise = (async () => {
      try {
        const value = await mutateAsync({ body: {} });
        const session = billingSessionSchema.parse(value);
        if (current()) setState({ scope, kind: "success", session });
      } catch {
        if (current()) setState({ scope, kind: "error" });
      } finally {
        if (active.current === lifetime) reset();
      }
    })();
    pending.current = { lifetime, promise };
    void promise.finally(() => {
      if (pending.current?.promise === promise) pending.current = null;
    });
    return promise;
  }, [scope, lifetime, mutateAsync, reset]);
  const visible = state?.scope === scope ? state : null;
  return {
    fetch,
    result: visible?.kind === "success" ? visible.session : null,
    loading: visible?.kind === "pending",
    error:
      visible?.kind === "error"
        ? t("settings.subscription.billingUnavailable")
        : null,
  };
};
